import React, { useEffect, useRef, useState } from "react"
import styled from "styled-components"
import Image from "gatsby-image"

import { debounce } from "../../../helpers/debounce"
import { IMember } from "../../../types/team"
import media from "../../../helpers/media"

/**
 * Member portrait section
 */
const MemberWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px 0;

  ${media.tablet} {
    flex-direction: row;
  }
`

const Portrait = styled(Image)`
  object-fit: cover;
  height: 100%;
  width: 100%;
`

const WIDTH_PORTRAIT = 280

const PortraitWrapper = styled.div`
  display: inline-block;
  width: 200px;
  height: 200px;
  border-radius: 50% !important;
  overflow: hidden;

  ${media.mobile} {
    width: ${WIDTH_PORTRAIT}px;
    height: ${WIDTH_PORTRAIT}px;
  }
`

const PortraitText = styled.div`
  display: inline-block;
  margin-top: 30px;

  ${media.tablet} {
    margin-top: 0;
    padding-left: 30px;
    width: calc(100% - ${WIDTH_PORTRAIT}px);
    max-width: 650px;
  }
`

interface IDimensions {
  w: number
  h: number
}

interface IExpand {
  dimensions: IDimensions
  duration: number
}

const Wrapper = styled.div<IExpand>`
  height: 0;
  opacity: 0;
  /* border-bottom: 1px solid var(--color-grey-1); */
  overflow: hidden;

  transition: all ${({ duration }) => duration}s ease-in-out;

  &.active {
    height: ${({ dimensions }) => dimensions.h}px;
    opacity: 1;
  }
`

const Name = styled.div`
  font-size: var(--font-size-xl);
  text-transform: lowercase;
`

const Role = styled.div`
  color: var(--color-grey-1);
  font-size: var(--font-size-md);
`

const Description = styled.div`
  margin-top: 25px;
  font-size: var(--font-size-md);
  font-family: var(--font-secondary);
`

interface IProps {
  duration: number
  selectedMember?: IMember
  portraitOpen: boolean
}

const ExpandSection = ({
  duration,
  selectedMember,
  portraitOpen,
}: IProps) => {
  const fullName = `${selectedMember?.firstName} ${selectedMember?.name}`

  const description =
    selectedMember?.description?.childContentfulRichText.html

  /**
   * Listen window resize event
   */

  // Store dimensions of window
  const [resize, setResize] = useState({
    height: typeof window !== "undefined" && window.innerHeight,
    width: typeof window !== "undefined" && window.innerWidth,
  })

  // Listen resize event
  useEffect(() => {
    const debouncedHandleResize = debounce(function handleResize() {
      setResize({
        height: window.innerHeight,
        width: window.innerWidth,
      })
    }, 500)

    window.addEventListener("resize", debouncedHandleResize)

    return () => {
      window.removeEventListener("resize", debouncedHandleResize)
    }
  })

  /**
   * Get height of Expand component to handle transition animation
   */

  // Expand component ref
  const refExpand = useRef<HTMLDivElement>(null)

  // Store dimensions of MemberWrapper
  const [expandDimensions, setExpandDimensions] = useState<
    IDimensions
  >({
    h: 0,
    w: 0,
  })

  useEffect(() => {
    const target = refExpand?.current?.querySelector("div")

    setExpandDimensions(() => {
      return {
        h: target?.offsetHeight || 0,
        w: target?.offsetWidth || 0,
      }
    })
  }, [selectedMember, portraitOpen, resize])

  if (!description) {
    return null
  }

  return (
    <Wrapper
      className={portraitOpen ? "active" : ""}
      dimensions={expandDimensions}
      duration={duration}
      ref={refExpand}
    >
      {selectedMember && (
        <MemberWrapper>
          {selectedMember.portrait && (
            <PortraitWrapper>
              <Portrait
                fluid={selectedMember.portrait.fluid}
                alt={`${fullName}`}
              />
            </PortraitWrapper>
          )}
          <PortraitText>
            <Name>{fullName}</Name>
            <Role>{selectedMember.roles?.join(" ")}</Role>
            {description && (
              <Description
                dangerouslySetInnerHTML={{
                  __html: description,
                }}
              />
            )}
          </PortraitText>
        </MemberWrapper>
      )}
      {!selectedMember && (
        <div>Aucune information supplémentaire</div>
      )}
    </Wrapper>
  )
}

export default ExpandSection
